/**
 * bodyMetrics.ts — расчёты по росту и весу посетителя: индекс массы тела,
 * границы нормального веса, уровень, процент снижения, дата достижения цели.
 *
 * Решение владельца №5 (2026-10-08, повтор воронки Slimkit Treadmill):
 * формулы и пороги — КАК У ЭТАЛОНА, у каждой константы — файл и строка
 * исходника (`slimkit-research/orig/...`, раскрыт из source map).
 *
 * ЧИСТЫЙ МОДУЛЬ: без React, DOM и window. Время («сегодня») приходит
 * параметром — иначе дату цели не проверить тестом.
 *
 * Единицы. У эталона одна система единиц на всю анкету (metric/imperial), у
 * нас — своя единица у каждого пикера (ответ + служебный ключ `<id>::unit`,
 * `answerCollection.pickerUnitValueKey`). Поэтому расчёт ведётся в системе
 * ЕДИНИЦЫ ВЕСА: вес в фунтах → рост в дюймах и формула эталона с 703; вес в
 * кг → рост в метрах. Рост и вес в одной системе (как всегда у эталона) —
 * числа совпадают с эталоном до знака. Разные системы — это у эталона
 * «посетитель переключил единицы», и значения переводятся ЕГО правилами с
 * его округлением (`orig/utils/unitConversion.ts:1-19`): см → целые дюймы,
 * футы/дюймы → целые см, кг ↔ целые фунты (×2.20462). Замер эталона:
 * 5 ft 7 in → «CM» 170, 187 lb → «KG» 85 (unit-measure-input.md §4).
 */
import { CM_PER_INCH, roundTo1, SLIMKIT_LB_PER_KG } from './unitConversion';

export type WeightUnit = 'kg' | 'lb';
export type HeightUnit = 'cm' | 'ft';

/** Рост: число в своей единице. Футы — дробное число футов (5 ft 6 in = 5.5). */
export interface HeightValue {
  value: number;
  unit: HeightUnit;
  /**
   * Дюймы, если рост введён парой «футы + дюймы» («5'7»). Нужны только
   * проверке пределов: у эталона дюймов не больше 12 (5 ft 13 in — ошибка).
   */
  inchesPart?: number;
}

export interface WeightValue {
  value: number;
  unit: WeightUnit;
}

// Фунтов в килограмме, сантиметров в дюйме и округление — в лёгком
// `unitConversion.ts` (их же берут правила логики в первом экране, WEB-2350).
export { SLIMKIT_LB_PER_KG, CM_PER_INCH, roundTo1 };
/** `orig/utils/health.ts:16, 36-37` — множитель ИМТ в фунтах и дюймах. */
export const BMI_IMPERIAL_FACTOR = 703;

/**
 * Пороги ИМТ эталона. Их ДВА набора, и это не ошибка разбора:
 *  - подсказка под вводом веса: недобор < 18.5, норма до 25 включительно,
 *    избыток < 30, дальше ожирение (`warningBMIMessage/index.tsx:56, 133-146`);
 *  - профиль и границы нормального веса: норма 18.5–28
 *    (`utils/health.ts:21-41`, `currentBMIV2/index.tsx:182-187, 216`).
 */
export const BMI_THRESHOLDS = {
  underweight: 18.5,
  hintNormalMax: 25,
  hintObese: 30,
  normalRangeMax: 28,
} as const;

/** `orig/utils/validators.ts:7-14` — допустимый рост. */
export const HEIGHT_LIMITS = {
  cm: { min: 100, max: 299 },
  ft: { min: 3, max: 9 },
  inch: { min: 0, max: 12 },
} as const;

/** `orig/utils/validators.ts:25-31` — допустимый вес (текущий и желаемый). */
export const WEIGHT_LIMITS = {
  kg: { min: 20, max: 299 },
  lb: { min: 44, max: 659 },
} as const;

/** `orig/components/chartV2/index.tsx:210, 271` — цель через 3 месяца. */
export const GOAL_DATE_MONTH_OFFSET = 3;

function toNumber(raw: string): number | null {
  const n = Number(raw.trim().replace(',', '.'));
  return Number.isFinite(n) ? n : null;
}

/**
 * Разбор ответа пикера роста. Принимает число в единице пикера («170»,
 * «5.5», «5,5») и запись «футы + дюймы» («5'7», «5' 7"», «5 ft 7 in») — её
 * даст будущий вид пикера с двумя полями (unit-measure-input.md §6).
 * Пусто, мусор, ноль и отрицательное → null.
 */
export function parseHeight(
  raw: string | undefined | null,
  unit: HeightUnit,
): HeightValue | null {
  if (raw == null) return null;
  const text = String(raw).trim();
  if (text === '') return null;
  if (unit === 'ft') {
    const m =
      /^(\d+(?:[.,]\d+)?)\s*(?:'|ft|фт)\s*(?:(\d+(?:[.,]\d+)?)\s*(?:"|''|in|дюйм\S*)?)?$/i.exec(
        text,
      );
    if (m) {
      const ft = Number(m[1].replace(',', '.'));
      const inch = m[2] ? Number(m[2].replace(',', '.')) : 0;
      const total = ft + inch / 12;
      return total > 0
        ? { value: total, unit, ...(m[2] ? { inchesPart: inch } : {}) }
        : null;
    }
  }
  const n = toNumber(text);
  return n != null && n > 0 ? { value: n, unit } : null;
}

/** Разбор ответа пикера веса: число в единице пикера. Пусто/мусор/≤0 → null. */
export function parseWeight(
  raw: string | undefined | null,
  unit: WeightUnit,
): WeightValue | null {
  if (raw == null) return null;
  const n = toNumber(String(raw));
  return n != null && n > 0 ? { value: n, unit } : null;
}

/** Рост в дюймах; из см — целые дюймы, как `cmToFtIn` эталона (`unitConversion.ts:1-6`). */
export function heightInInches(h: HeightValue): number {
  return h.unit === 'ft' ? h.value * 12 : Math.round(h.value / CM_PER_INCH);
}

/** Рост в метрах; из футов — целые см, как `ftInToCm` эталона (`unitConversion.ts:8-11`). */
export function heightInMeters(h: HeightValue): number {
  return h.unit === 'cm'
    ? h.value / 100
    : Number((h.value * 12 * CM_PER_INCH).toFixed(0)) / 100;
}

/**
 * Вес в нужной единице; между кг и фунтами — целым числом, как `kgToLbs` /
 * `lbsToKg` эталона (`unitConversion.ts:13-19`).
 */
export function weightIn(w: WeightValue, unit: WeightUnit): number {
  if (w.unit === unit) return w.value;
  return unit === 'lb'
    ? Number((w.value * SLIMKIT_LB_PER_KG).toFixed(0))
    : Number((w.value / SLIMKIT_LB_PER_KG).toFixed(0));
}

/**
 * Рост в допустимых пределах (`validators.ts:3-15`). Футы у нас дробные:
 * 3 ft 0 in … 9 ft 12 in = 36 … 120 дюймов.
 */
export function isHeightValid(h: HeightValue): boolean {
  if (h.unit === 'cm') {
    return h.value >= HEIGHT_LIMITS.cm.min && h.value <= HEIGHT_LIMITS.cm.max;
  }
  if (
    h.inchesPart != null &&
    (h.inchesPart < HEIGHT_LIMITS.inch.min ||
      h.inchesPart > HEIGHT_LIMITS.inch.max)
  ) {
    return false;
  }
  const inches = h.value * 12;
  return (
    inches >= HEIGHT_LIMITS.ft.min * 12 &&
    inches <= HEIGHT_LIMITS.ft.max * 12 + HEIGHT_LIMITS.inch.max
  );
}

/** Вес в допустимых пределах (`validators.ts:21-32`). */
export function isWeightValid(w: WeightValue): boolean {
  const lim = WEIGHT_LIMITS[w.unit];
  return w.value >= lim.min && w.value <= lim.max;
}

/**
 * ИМТ без округления (`utils/health.ts:4-19`): фунты → `lb / in² · 703`,
 * килограммы → `kg / m²`. Система — по единице веса (см. шапку).
 */
export function calcBmi(h: HeightValue, w: WeightValue): number | null {
  if (w.unit === 'lb') {
    const inches = heightInInches(h);
    if (!(inches > 0)) return null;
    return (w.value / (inches * inches)) * BMI_IMPERIAL_FACTOR;
  }
  const m = heightInMeters(h);
  if (!(m > 0)) return null;
  return w.value / (m * m);
}

export type BmiLevel = 'low' | 'normal' | 'high';

/**
 * Уровень для профиля (`currentBMIV2/index.tsx:216`): по ОКРУГЛЁННОМУ ИМТ,
 * ниже 18.5 — низкий, выше 28 — высокий, иначе нормальный.
 */
export function bmiLevel(bmiRounded: number): BmiLevel {
  if (bmiRounded < BMI_THRESHOLDS.underweight) return 'low';
  if (bmiRounded > BMI_THRESHOLDS.normalRangeMax) return 'high';
  return 'normal';
}

export type BmiCategory = 'underweight' | 'normal' | 'overweight' | 'obese';

/**
 * Категория для подсказки под вводом веса (`warningBMIMessage/index.tsx:56,
 * 133-146`): по ИМТ БЕЗ округления; норма 18.5–25 включительно.
 */
export function bmiCategory(bmi: number): BmiCategory {
  if (bmi < BMI_THRESHOLDS.underweight) return 'underweight';
  if (bmi <= BMI_THRESHOLDS.hintNormalMax) return 'normal';
  if (bmi < BMI_THRESHOLDS.hintObese) return 'overweight';
  return 'obese';
}

/**
 * Границы нормального веса для роста, в единице `unit`, округлённые
 * (`utils/health.ts:21-41`, `currentBMIV2/index.tsx:178-188`):
 * фунты — `18.5 · in² / 703` … `28 · in² / 703`; кг — `18.5 · m²` … `28 · m²`.
 */
export function normalWeightRange(
  h: HeightValue,
  unit: WeightUnit,
): { min: number; max: number } {
  if (unit === 'lb') {
    const inches = heightInInches(h);
    return {
      min: Math.round(
        (BMI_THRESHOLDS.underweight * inches * inches) / BMI_IMPERIAL_FACTOR,
      ),
      max: Math.round(
        (BMI_THRESHOLDS.normalRangeMax * inches * inches) / BMI_IMPERIAL_FACTOR,
      ),
    };
  }
  const m = heightInMeters(h);
  return {
    min: Math.round(BMI_THRESHOLDS.underweight * m * m),
    max: Math.round(BMI_THRESHOLDS.normalRangeMax * m * m),
  };
}

/**
 * Процент снижения от текущего до желаемого (`warningBMIMessage/index.tsx:165-166`):
 * `round1((текущий − желаемый) / текущий · 100)`. Желаемый переводится в
 * единицу текущего. Текущего нет → null.
 */
export function weightLossPercent(
  current: WeightValue,
  goal: WeightValue,
): number | null {
  if (!(current.value > 0)) return null;
  const goalInCurrent = weightIn(goal, current.unit);
  return roundTo1(((current.value - goalInCurrent) / current.value) * 100);
}

export interface GoalDateParts {
  /** День месяца — как у эталона, после `setMonth` (переполнение: 30.11 → 2 марта). */
  day: number;
  /** Месяц 0–11 — БЕЗ переполнения (`getMonthMeta`): у 30.11 это февраль. */
  monthIndex: number;
  year: number;
  /** Год цели совпадает с текущим — тогда год в подписи не пишется. */
  sameYear: boolean;
}

/**
 * Дата достижения цели (`chartV2/index.tsx:120-130, 210, 271, 278-285`):
 * сегодня + 3 месяца. Месяц и год — арифметикой месяцев, день — из
 * `Date.setMonth` (в конце месяца даёт «лишние» дни — повторяем как есть).
 */
export function goalDateParts(
  now: Date,
  monthOffset: number = GOAL_DATE_MONTH_OFFSET,
): GoalDateParts {
  const total = now.getMonth() + monthOffset;
  const year = now.getFullYear() + Math.floor(total / 12);
  const monthIndex = ((total % 12) + 12) % 12;
  const finalDate = new Date(now.getTime());
  finalDate.setMonth(now.getMonth() + monthOffset);
  return {
    day: finalDate.getDate(),
    monthIndex,
    year,
    sameYear: year === now.getFullYear(),
  };
}

/**
 * Название месяца на языке воронки — в той форме, в какой оно стоит рядом с
 * числом (у русского — родительный падеж: «января»). Берётся у Intl, своих
 * таблиц месяцев не держим.
 */
export function monthNameForDay(
  locale: string,
  monthIndex: number,
  year: number,
): string {
  try {
    const parts = new Intl.DateTimeFormat(locale, {
      day: 'numeric',
      month: 'long',
    }).formatToParts(new Date(year, monthIndex, 15));
    return parts.find((p) => p.type === 'month')?.value ?? '';
  } catch {
    return '';
  }
}

/**
 * Подпись даты цели по правилам эталона (`formatChartDayMonth`,
 * `chartV2/index.tsx:37-90`) с его же склейкой года: у месяца ДРУГОГО года
 * к названию месяца дописывается год (`getMonthFullLabelWithYear`, :219-224; тот же месяц — `monthsFull` :205).
 * Английский: «January 2027 8» — именно так на живом экране 53
 * (`screens2/53/dump.json`, снят 2026-10-08). Русский — общая ветка эталона
 * «день месяц»: «8 января 2027».
 */
export function formatGoalDate(parts: GoalDateParts, locale: string): string {
  const lang = (locale || 'en').toLowerCase();
  const month = monthNameForDay(lang, parts.monthIndex, parts.year);
  const monthLabel = parts.sameYear ? month : `${month} ${parts.year}`;
  const y = (suffix: string) =>
    parts.sameYear ? '' : `${parts.year}${suffix}`;
  if (lang.startsWith('zh') || lang.startsWith('ja')) {
    return `${y('年')}${parts.monthIndex + 1}月${parts.day}日`;
  }
  if (lang.startsWith('ko'))
    return `${y('년 ')}${parts.monthIndex + 1}월 ${parts.day}일`;
  if (lang.startsWith('en')) return `${monthLabel} ${parts.day}`;
  if (lang.startsWith('de')) return `${parts.day}. ${monthLabel}`;
  if (lang.startsWith('pt') || lang.startsWith('es'))
    return `${parts.day} de ${monthLabel}`;
  return `${parts.day} ${monthLabel}`;
}

/** «Месяц год» без дня (для заголовков вида «к январю 2027»): Intl по языку воронки. */
export function formatGoalMonthYear(
  parts: GoalDateParts,
  locale: string,
): string {
  try {
    return new Intl.DateTimeFormat(locale || 'en', {
      month: 'long',
      year: 'numeric',
    }).format(new Date(parts.year, parts.monthIndex, 15));
  } catch {
    return '';
  }
}
